// src/modules/app-views/leads/pages/Init/modals/LeadsParametersModal/modals/AddDataSourceModal/Step1.jsx
// *******************************************************************************
// Step 1: Source identification fields (name, url, token)
// *******************************************************************************
import { useCallback } from 'react';
import { KeyOutlined } from '@ant-design/icons';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleWebsite from 'components/SimpleComponents/SimpleWebsite';
import SimpleInputWithButton from 'components/SimpleComponents/SimpleInputWithButton';
import translateX from 'utils/translateX';

// UUID v4 regex for inline validation
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// *******************************************************************************
// STEP CONTENT
// *******************************************************************************
const Step1Content = () => {
  const { form } = useSimpleFormContext();

  const handleGenerateToken = useCallback(() => {
    form?.setFieldValue('token', generateUUIDv4());
  }, [form]);

  return (
    <>
      <SimpleInput
        name="name"
        label="name"
        required
        maxLength={100}
        placeholder="data_source_name_placeholder"
        autoFocus
      />

      <SimpleInput
        name="description"
        label="description"
        maxLength={300}
        placeholder="data_source_description_placeholder"
      />

      <SimpleWebsite
        name="url"
        label="URL"
        required
        maxLength={500}
      />

      <SimpleInputWithButton
        name="token"
        label="Token"
        tooltip={translateX('token_auto_generated')}
        required
        maxLength={50}
        placeholder="token_paste_or_leave_blank"
        style={{ fontFamily: 'monospace' }}
        buttonIcon={<KeyOutlined />}
        buttonTooltip="generate_token"
        onButtonClick={handleGenerateToken}
        rules={[
          {
            required: true,
            message: () => translateX('required_field'),
          },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              if (UUID_V4_REGEX.test(value)) return Promise.resolve();
              return Promise.reject(new Error(translateX('invalid_uuid')));
            },
          },
        ]}
      />
    </>
  );
};

// *******************************************************************************
// STEP BUILDER
// *******************************************************************************
const step1 = ({ isEditing } = {}) => ({
  stepTitle: isEditing ? 'edit_data_source' : 'add_data_source',
  content: () => <Step1Content />,
  validationFields: ['name', 'url', 'token'],
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step1;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function generateUUIDv4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback manual UUID v4 generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}
